// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ShipmentQuarantineResolution,
  ShipmentQuarantineResolutionProperty,
} from '@/audit/api/types';
import { Link } from '@/components/link';
import { Stack, Typography } from '@mui/material';

interface Props {
  property: ShipmentQuarantineResolutionProperty;
}

const RESOLUTION_LABELS: Record<string, string> = {
  KEPT_HARDFIN_ITEM: 'Kept Hardfin item',
  CORRECTED_SERIAL: 'Corrected serial',
  CREATED_ASSET: 'Created asset',
  ACCEPTED_SERIAL: 'Accepted serial',
};

type ResolutionItem = ShipmentQuarantineResolution['resolvedItem'];
type ResolutionAsset = ShipmentQuarantineResolution['resolvedAsset'];

/**
 * Formats an item as "sku (name)", falling back to the name for items with no
 * SKU.
 */
function itemLabel(item: ResolutionItem): string {
  return item.sku ? `${item.sku} (${item.name})` : item.name;
}

/**
 * Renders a serial, linked to its asset when Hardfin has one.
 */
function SerialLink(props: { asset: ResolutionAsset; fallback?: string }) {
  if (props.asset === null) {
    return <>{props.fallback ?? '—'}</>;
  }

  return (
    <Link to="/assets/$assetKey" params={{ assetKey: props.asset.key }}>
      {props.asset.serial}
    </Link>
  );
}

/**
 * One labelled line of the resolution detail.
 */
function DetailRow(props: { label: string; children: React.ReactNode }) {
  return (
    <Typography variant="caption" marginLeft={2}>
      {props.label}: {props.children}
    </Typography>
  );
}

/** Renders how a held serial was resolved, shaped per resolution. */
export function AuditShipmentQuarantineResolutionLabel(props: Props) {
  const resolution = props.property.newValue;
  if (resolution === null || resolution === undefined) {
    return null;
  }

  const decision = RESOLUTION_LABELS[resolution.kind] ?? resolution.kind;
  const correction = resolution.correction;

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      <DetailRow label="Resolution">{decision}</DetailRow>

      {resolution.kind === 'CORRECTED_SERIAL' ? (
        <>
          <DetailRow label="Item">
            {itemLabel(resolution.resolvedItem)}
          </DetailRow>
          <DetailRow label="Original 3PL serial">
            <SerialLink
              asset={correction?.old.asset ?? null}
              fallback={resolution.serial}
            />
          </DetailRow>
          <DetailRow label="Corrected Hardfin serial">
            <SerialLink asset={resolution.resolvedAsset} />
          </DetailRow>
        </>
      ) : resolution.kind === 'CREATED_ASSET' ||
        resolution.kind === 'ACCEPTED_SERIAL' ? (
        <>
          <DetailRow label="Serial">
            <SerialLink
              asset={resolution.resolvedAsset}
              fallback={resolution.serial}
            />
          </DetailRow>
          <DetailRow label="Created as item">
            {itemLabel(resolution.resolvedItem)}
          </DetailRow>
        </>
      ) : (
        <>
          <DetailRow label="Serial">
            <SerialLink
              asset={resolution.resolvedAsset}
              fallback={resolution.serial}
            />
          </DetailRow>
          {correction !== null && (
            <DetailRow label="Original 3PL item">
              {itemLabel(correction.old.item)}
            </DetailRow>
          )}
          <DetailRow label="Kept Hardfin item">
            {itemLabel(resolution.resolvedItem)}
          </DetailRow>
        </>
      )}

      {resolution.note && (
        <Typography variant="caption" marginLeft={2}>
          Note: {resolution.note}
        </Typography>
      )}
    </Stack>
  );
}
